import { groupBy, ListIteratee, Many, orderBy, ValueIteratee } from 'lodash';
import Logger from '@/modules/core/app/helpers/Logger';
import { BaseModel } from '/@/modules/core/app/models/abstracts/BaseModel';

export class ModelCollection<T extends BaseModel> {
  items?: T[];

  private readonly model: typeof BaseModel & (new (model?: Partial<T>) => T);

  constructor(
    items: Partial<T>[] | ModelCollection<T> | null | undefined,
    model: typeof BaseModel & (new (model?: Partial<T>) => T)
  ) {
    if (items instanceof ModelCollection) {
      this.items = items.get();
    } else {
      this.items =
        items?.length && !(items[0] instanceof model) ? model.mapModels(items) : (items as T[]);
    }

    this.model = model;
  }

  get(): T[] | undefined {
    return this.items;
  }

  get count(): number {
    return this.items?.length ?? 0;
  }

  get hasItems(): boolean {
    return this.count > 0;
  }

  set(items: T[]): void {
    this.items = items;
  }

  get first(): T | null {
    return this.items?.length ? this.items[0] : null;
  }

  get last(): T | null {
    return this.items?.length ? this.items[this.items?.length - 1] : null;
  }

  add(item: Partial<T>, index?: number): void {
    const newItem: T = !(item instanceof this.model) ? this.model.mapModel(item) : (item as T);

    if (!this.items) {
      this.items = [];
    }

    index === -1 || index === undefined
      ? this.items.push(newItem)
      : this.items.splice(index, 1, newItem);
  }

  addUnique(item: Partial<T>, key?: keyof T): void {
    if (!item.id || this.findByKey(item.id, key)) {
      return;
    }

    this.add(item);
  }

  append(items: T[] = []): void {
    this.items?.push(...items);
  }

  appendUnique(items: T[] = []): void {
    items.forEach((item: T): void => {
      if (!!item.id && !this.findByKey(item.id)) {
        this.items?.push(item);
      }
    });
  }

  update(itemToUpdate: Partial<T>, key?: keyof T): void {
    if (!this.items) {
      Logger.errors('No items to update');
      return;
    }
    const prop: keyof T = key ?? ('id' as keyof T);
    const index: number = this.items.findIndex((item: T) => item[prop] === itemToUpdate[prop]);
    if (index > -1) {
      this.items.splice(index, 1, this.model.mapModel(itemToUpdate));
    } else {
      Logger.errors('Item could not be updated:', itemToUpdate);
    }
  }

  save(itemToSave: Partial<T>, key?: keyof T): void {
    if (itemToSave.id) {
      this.update(itemToSave, key);
      return;
    }

    this.add(itemToSave);
  }

  upsert(item: Partial<T>, key?: keyof T): void {
    const prop: keyof T = key ?? ('id' as keyof T);
    const value: T[keyof T] | undefined = item[prop];

    if (value !== undefined && this.findByKey(value, key)) {
      this.update(item, key);
      return;
    }

    this.add(item);
  }

  findByKey(id: unknown, key?: keyof T): T | undefined {
    return this.items?.find((item: T): boolean => item[key ?? ('id' as keyof T)] === id);
  }

  findByIndex(index: number): T | undefined {
    return this.items?.[index];
  }

  delete(id: unknown, key?: keyof T): void {
    this.items = this.items?.filter((item: T): boolean => item[key ?? ('id' as keyof T)] !== id);
  }

  groupBy(iteratee: ValueIteratee<T>): Array<{ key: string | number; items: T[] }> {
    const groupData: Array<{ key: string | number; items: T[] }> = [];

    for (const [key, items] of Object.entries(groupBy(this.items, iteratee))) {
      groupData.push({ key, items });
    }

    return groupData;
  }

  orderBy(
    iteratees: Many<ListIteratee<T>>,
    orders: Many<boolean | 'asc' | 'desc'>
  ): ModelCollection<T> {
    const sortedItems: T[] = orderBy(this.items, iteratees, orders);
    return new ModelCollection(sortedItems, this.model) as ModelCollection<T>;
  }

  unshift(item: Partial<T>): void {
    const newItem: T = !(item instanceof this.model) ? this.model.mapModel(item) : (item as T);

    if (!this.items) {
      this.items = [];
    }

    this.items.unshift(newItem);
  }

  clear(): void {
    this.set([]);
  }

  toString(): string {
    return this.items?.map((item: T): string => item.toString() ?? '').join(', ') ?? '';
  }

  toJSON(): T[] | undefined {
    return this.items;
  }
}
